/* The waiting gesture — variant B, "Pulse", from
 * `docs/design/2026-09-18-loading-animations-mockup.html`. The owner first picked E ("Rep") and
 * withdrew it the same day as too busy; B is what ships. The travel, the hold and the curves below
 * are read off that file.
 *
 * ── What is drawn ───────────────────────────────────────────────────────────────────────────
 * Two copies of the mark in one cell. The GHOST is the mark at 14% ink and never moves. Over it,
 * a BAND — a box leaning at the angle of the mark's own strokes, soft at both ends — travels left
 * to right, and the full-ink mark is visible only through it. So ink runs into the mark from the
 * first stroke to the head, holds, and drains out the same way.
 *
 * ── Transform only, and why the ink copy is animated too ────────────────────────────────────
 * The band moves by `transform`, which is the only thing this app lets a looping animation touch.
 * The full-ink mark is the band's CHILD, so it would travel with it; it carries the exact inverse
 * transform on the same keyframes and the same curves, about the same point in space
 * (`transform-origin: 85% 50%` is the band's centre, written in the child's own box), and therefore
 * stands still while its window passes over it. `skewX(38deg) translateX(-x)` is the inverse of
 * `translateX(x) skewX(-38deg)`; the child's percentages are the band's × 1.7 because the band is
 * 1.7 marks wide and a percentage translate is of the element's own width.
 *
 * ── The mark is the ASSET ───────────────────────────────────────────────────────────────────
 * Both copies are `shared/ui/brandMark.module.css` — the app never draws its own logo, and that
 * sheet is the one construction. This file supplies size, opacity and transform and restates none
 * of the mask properties and none of the ink. `composes` is deliberately not used: no stylesheet
 * in this app uses it and Stylelint does not know the property; `BrandLoader.tsx` names both
 * classes on one element, the call `ExerciseAuthorMark` already makes.
 *
 * ── It appears only when the wait is real ───────────────────────────────────────────────────
 * Owner, 18 September 2026: the loader is for screens that genuinely take long, not a flash on
 * every navigation. The root starts at opacity 0 and fades in after `--delay-loader-grace`, so a
 * read that answers inside the grace draws nothing at all, and one that answers just after it is
 * unmounted while still near-transparent. This is a gate, the same idea as `loadingGate.ts`'s
 * grace for skeletons, and it hides only the DRAWING: the box is there from the first frame, so
 * nothing moves, and the sr-only sentence is there from the first frame, so the announcement is
 * not delayed.
 *
 * ── `prefers-reduced-motion` ────────────────────────────────────────────────────────────────
 * Handled once, in `design-system/tokens/base.css`, which collapses every animation to 0.001ms and
 * ONE iteration. The resting state — these rules with no keyframe applied — is the still a
 * motion-averse viewer is left with: the band parked over the mark, so the mark stands whole in
 * full ink. The entrance keeps its delay and `forwards`, so the still also waits out the grace and
 * then simply appears.
 */

.loader {
  position: relative;
  display: grid;
  width: var(--m);
  height: var(--m);
  place-items: center;
  opacity: 0;
  animation: loader-in var(--dur-loader-in) var(--ease-default) var(--delay-loader-grace) forwards;
}

/* The mockup's full-screen size. */
.screen {
  --m: 76px;
}

/* A card, a dialog or the session panel's slot. */
.panel {
  --m: 54px;
}

@keyframes loader-in {
  to {
    opacity: 1;
  }
}

.mark {
  width: var(--m);
  height: var(--m);
}

.ghost {
  grid-area: 1 / 1;
  opacity: 0.14;
}

/* 1.7 marks wide and 1.2 tall: wide enough that its solid middle covers the whole glyph at the
 * hold, tall enough that the lean never uncovers a corner. The mask is static and travels with
 * the box; it is what makes the ink arrive and leave as a soft front rather than a hard edge. */
.band {
  position: absolute;
  top: -10%;
  left: 0;
  width: 170%;
  height: 120%;
  overflow: hidden;
  mask-image: linear-gradient(90deg, rgb(0 0 0 / 0%) 0%, rgb(0 0 0 / 100%) 15%, rgb(0 0 0 / 100%) 85%, rgb(0 0 0 / 0%) 100%);
  transform: translateX(-20.6%) skewX(-38deg);
  animation: pulse-band var(--dur-pulse) var(--ease-default) infinite;
}

/* The band is 1.2 marks tall and starts 0.1 above, so the ink copy sits 0.1 marks down inside it
 * to land exactly on the ghost: 10 / 120 of the band's height. */
.ink {
  position: absolute;
  top: 8.3333%;
  left: 0;
  transform: skewX(38deg) translateX(35.02%);
  transform-origin: 85% 50%;
  animation: pulse-ink var(--dur-pulse) var(--ease-default) infinite;
}

@keyframes pulse-band {
  0% {
    transform: translateX(-108%) skewX(-38deg);
    animation-timing-function: var(--ease-pulse-in);
  }

  44% {
    transform: translateX(-20.6%) skewX(-38deg);
    animation-timing-function: var(--ease-linear);
  }

  50% {
    transform: translateX(-20.6%) skewX(-38deg);
    animation-timing-function: var(--ease-pulse-out);
  }

  100% {
    transform: translateX(70%) skewX(-38deg);
  }
}

/* The inverse of `pulse-band`, stop for stop and curve for curve. Change one, change both. */
@keyframes pulse-ink {
  0% {
    transform: skewX(38deg) translateX(183.6%);
    animation-timing-function: var(--ease-pulse-in);
  }

  44% {
    transform: skewX(38deg) translateX(35.02%);
    animation-timing-function: var(--ease-linear);
  }

  50% {
    transform: skewX(38deg) translateX(35.02%);
    animation-timing-function: var(--ease-pulse-out);
  }

  100% {
    transform: skewX(38deg) translateX(-119%);
  }
}
